import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  IconButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Image,
  Button,
  Select,
  useToast,
  Text,
  RadioGroup,
  Radio,
  Stack,
  Switch,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { uploadSupplierLogo, validateLogoFile, LOGO_ACCEPT, LOGO_MAX_BYTES } from "../../../tools/supplierLogo";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

// Type-first creation form (TASK-user-creation-rework phase 1). The old form
// showed every field for every type; this one renders only what the chosen
// type needs, and the backend contract is keyed on user_type — no more
// inferring supplier-ness from the retailer toggles.
//
// Creatable types: CAM / supplier / org_admin. "Basic" is gone from here —
// org admins create their own org viewers; masters are provisioned manually.
const USER_TYPES = [
  { value: "CAM", label: "CAM" },
  { value: "supplier", label: "Supplier" },
  { value: "org_admin", label: "Organisation Admin" },
];

const RETAILERS = ["PNP", "CHK", "DIS", "MAK", "CLI", "MED", "PET"];

// Display labels for page names that aren't already readable as-is.
const PAGE_LABELS = { StrikeRate: "Strike Rate", MyProfile: "My Profile", Edi: "EDI", DataStatus: "Data Status", LoginHistory: "History" };
const pageLabel = (p) => PAGE_LABELS[p] || p;

// Fallbacks in case the API predates reportPages/camAdminPages (paired deploy,
// but a stale bundle must not render empty pickers).
const DEFAULT_REPORT_PAGES = ["Sales", "Insights", "StrikeRate", "Ranging", "Margin", "MyProfile"];
const DEFAULT_CAM_ADMIN_PAGES = ["Articles", "Imports", "Edi", "DataStatus", "Validation", "Reports"];
// The org admin's own admin screens (Users / Articles / Batch Reports). This
// form never offered them, so every org admin created here landed without a
// Users page and the master had no screen to add it (fixed 2026-09-16).
// Pre-selected: an org admin without them cannot do the job.
const DEFAULT_ORG_ADMIN_PAGES = ["Users", "Articles", "Reports"];

// What the existing accounts all carry — a sensible starting grant, still toggleable.
const PRESELECTED_PAGES = ["Sales", "StrikeRate", "Ranging", "Margin", "MyProfile"];

const TogglePill = ({ on, onClick, children }) => (
  <Button variant={on ? "solid" : "outline"} colorScheme={on ? "green" : "gray"} mr={2} mb={2} onClick={onClick}>
    {children}
  </Button>
);

const AddUser = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedOrg, organisations, isMaster, refreshSupplierList } = useSupplierRetailer();

  // The Users page's contextual "Add {type}" buttons pass ?type= and lock the
  // selector; opening /addUser directly keeps the free choice.
  const [searchParams] = useSearchParams();
  const lockedType = USER_TYPES.some((t) => t.value === searchParams.get("type")) ? searchParams.get("type") : null;

  const [userType, setUserType] = useState(lockedType || "CAM");
  const [setupMode, setSetupMode] = useState("invite"); // 'invite' | 'password'

  const [fname, setFname] = useState("");
  const [codename, setCodename] = useState(""); // supplier code (supplier type only)
  const [username, setUsername] = useState("");
  const [useremail, setUseremail] = useState("");
  const [password, setPassword] = useState("");

  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [reportPages, setReportPages] = useState(DEFAULT_REPORT_PAGES);
  const [camAdminPages, setCamAdminPages] = useState(DEFAULT_CAM_ADMIN_PAGES);
  const [orgAdminPages, setOrgAdminPages] = useState(DEFAULT_ORG_ADMIN_PAGES);
  // Organisation an org admin is created in — picked on the form (defaults to
  // the master's toggle) so a forgotten toggle cannot file the admin under the
  // wrong organisation. Other types still follow the toggle.
  const [adminOrg, setAdminOrg] = useState("");

  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedPages, setSelectedPages] = useState(PRESELECTED_PAGES);
  const [selectedAdminPages, setSelectedAdminPages] = useState([]);
  // Articles grant level for CAMs / org admins (2026-09-17): 'rw' | 'ro'.
  const [articlesLevel, setArticlesLevel] = useState("rw");
  const [retailerAccess, setRetailerAccess] = useState({});

  const [defaultSupplier, setDefaultSupplier] = useState("");
  const [defaultRetailer, setDefaultRetailer] = useState("");

  const [paymentRequired, setPaymentRequired] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(""); // Rands; sent to the API in cents

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCam = userType === "CAM";
  const isSupplier = userType === "supplier";
  const isOrgAdmin = userType === "org_admin";
  const isInvite = setupMode === "invite";

  // The organisation the new account lands in: the master's toggle, except an
  // org admin picks it explicitly on the form.
  const targetOrg = isOrgAdmin && adminOrg ? adminOrg : selectedOrg;
  const orgOptions = organisations.length ? organisations : selectedOrg ? [{ org_code: selectedOrg, org_name: selectedOrg }] : [];

  const supplierCode = codename.trim().toUpperCase();
  // The grants the account will hold — a supplier always (and only) sees itself.
  const grantedSuppliers = isSupplier ? (supplierCode ? [supplierCode] : []) : selectedSuppliers;
  const enabledRetailers = RETAILERS.filter((r) => retailerAccess[r.toLowerCase()]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-suppliers-and-pages`);
        const { suppliers, reportPages: rp, camAdminPages: cap, orgAdminPages: oap } = response.data.data;
        // Grant list scoped to the organisation the account is created in;
        // suppliers without an org (pre-migration) stay grantable everywhere.
        // DEMO is never offered: only masters and the demo login may hold it,
        // and nothing created here is either (the backend strips it too).
        // Membership, not just the home org (2026-09-17): `org_codes` lists every
        // organisation the supplier is visible in, home first.
        const memberOf = (item, org) => {
          const orgs = Array.isArray(item.org_codes) && item.org_codes.length ? item.org_codes : item.org_code ? [item.org_code] : [];
          return orgs.length === 0 || orgs.includes(org);
        };
        const inOrg = (targetOrg ? suppliers.filter((item) => memberOf(item, targetOrg)) : suppliers).filter(
          (item) => String(item.suppliercode || "").toUpperCase() !== "DEMO"
        );
        setAvailableSuppliers(inOrg.map((item) => item.suppliercode));
        if (Array.isArray(rp) && rp.length) setReportPages(rp);
        if (Array.isArray(cap) && cap.length) setCamAdminPages(cap);
        // The org admin's admin screens = its grantable set minus the report pages.
        if (Array.isArray(oap) && oap.length) {
          const reports = Array.isArray(rp) && rp.length ? rp : DEFAULT_REPORT_PAGES;
          const adminOnly = oap.filter((p) => !reports.includes(p));
          if (adminOnly.length) setOrgAdminPages(adminOnly);
        }
      } catch (error) {
        toast({
          title: "Failed to load data",
          description: "An error occurred while loading data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchData();
  }, [toast, targetOrg]);

  // Defaults are picked FROM the grants — a default the account can't open was
  // the classic blank-screen landmine, so the selects stay constrained.
  useEffect(() => {
    if (!grantedSuppliers.includes(defaultSupplier)) setDefaultSupplier(grantedSuppliers[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grantedSuppliers.join(",")]);

  useEffect(() => {
    if (!enabledRetailers.includes(defaultRetailer)) setDefaultRetailer(enabledRetailers[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledRetailers.join(",")]);

  // Org admins get their admin screens pre-selected (still toggleable); the
  // organisation select starts on the master's toggle.
  useEffect(() => {
    // History (LoginHistory) and Alerts are grantable but not pre-selected —
    // they are the master's call per organisation (2026-09-17).
    if (userType === "org_admin") setSelectedAdminPages(orgAdminPages.filter((p) => p !== "LoginHistory" && p !== "Alerts"));
    else if (userType !== "CAM") setSelectedAdminPages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, orgAdminPages.join(",")]);

  useEffect(() => {
    if (!adminOrg && selectedOrg) setAdminOrg(selectedOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  const handleUserTypeChange = (e) => {
    const next = e.target.value;
    setUserType(next);
    if (next !== "supplier") setCodename("");
    if (next !== "CAM" && next !== "org_admin") setSelectedAdminPages([]);
  };

  const toggleIn = (setter) => (value) => setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  const handleSupplierToggle = toggleIn(setSelectedSuppliers);
  const handlePageToggle = toggleIn(setSelectedPages);
  const handleAdminPageToggle = toggleIn(setSelectedAdminPages);

  const handleRetailerToggle = (retailer) => {
    setRetailerAccess((prev) => ({
      ...prev,
      [retailer]: !prev[retailer],
    }));
  };

  // Revoke the preview blob when it is replaced or the form unmounts.
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    const problem = validateLogoFile(file);
    if (problem) {
      toast({ title: "Logo not accepted", description: problem, status: "warning", duration: 5000, isClosable: true });
      e.target.value = "";
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const clearLogo = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const problemWithForm = () => {
    if (isSupplier && !supplierCode) return "Enter the supplier code.";
    if (isInvite && !useremail.trim()) return "Enter the email address to send the invite to.";
    if (!isInvite && (!username.trim() || !password)) return "Enter a username and password (or switch to an email invite).";
    if (!isOrgAdmin && enabledRetailers.length === 0) return "Select at least one retailer.";
    // An org admin may be created before its organisation has any supplier —
    // suppliers created in the org later are granted to it automatically.
    if (!isSupplier && !isOrgAdmin && grantedSuppliers.length === 0) return "Grant access to at least one supplier.";
    if (isOrgAdmin && !targetOrg) return "Select the organisation.";
    if (paymentRequired && !(parseFloat(paymentAmount) > 0)) return "Enter the monthly amount for the payment subscription.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = problemWithForm();
    if (problem) {
      toast({ title: "Not quite ready", description: problem, status: "warning", duration: 5000, isClosable: true });
      return;
    }
    setIsSubmitting(true);
    const payload = {
      user_type: userType,
      invite: isInvite,
      fname: fname.trim(),
      codename: supplierCode,
      username: username.trim(),
      useremail: useremail.trim(),
      password,
      suppliers: grantedSuppliers,
      pages: [...selectedPages, ...(isCam || isOrgAdmin ? selectedAdminPages : [])],
      // Read-only Articles for a CAM / org admin (2026-09-17); ignored otherwise.
      page_levels: isCam || isOrgAdmin ? { Articles: articlesLevel } : undefined,
      retailerAccess: isOrgAdmin ? {} : retailerAccess,
      default_supplier: defaultSupplier,
      default_retailer: defaultRetailer,
      subscription: { enabled: paymentRequired, amount: paymentRequired ? Math.round((parseFloat(paymentAmount) || 0) * 100) : 0 },
      // New users/suppliers land in the organisation the master admin has
      // toggled; an org admin lands in the organisation picked on the form.
      org_code: targetOrg,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/ins-user`, payload);

      if (response.data.status === "error") {
        toast({ title: response.data.title || "Failed to add user", description: response.data.message, status: "error", duration: 8000, isClosable: true });
        return;
      }

      toast({
        title: response.data.title || "Success!",
        description: response.data.message,
        // A failed invite email still created the account — surface the manual
        // link long enough to copy it.
        status: response.data.data?.emailed === false ? "warning" : "success",
        duration: response.data.data?.emailed === false ? 15000 : 4000,
        isClosable: true,
      });

      // A new supplier exists now, but the supplier dropdown was loaded at app
      // boot — refresh the list in place (fire-and-forget, selection untouched)
      // so it appears without a full page reload.
      if (isSupplier) refreshSupplierList();

      // The account exists at this point — a logo failure must not read as a
      // failed insert, so it is reported separately and does not block navigation.
      // Kept as a second request so ins-user stays a plain JSON call.
      const { userid, suppliercode } = response.data?.data || {};
      let logoCode = null;
      let logoContext = null;
      if (logoFile) {
        if (isSupplier && suppliercode) {
          logoCode = suppliercode;
          logoContext = `Shown in the supplier selector immediately.`;
        } else if (isOrgAdmin) {
          // Organisation logo — stored under ORG_<code> so it can never collide
          // with a real supplier's logo. Drives the sidebar brand mark.
          logoCode = `ORG_${targetOrg}`;
          logoContext = `${targetOrg} branding will show for its users.`;
        } else if (isCam && userid) {
          // CAM logo — stored under CAM_<userid>; replaces the rail mark for
          // this account only (tools/brandMark.js).
          logoCode = `CAM_${userid}`;
          logoContext = `The sidebar will carry this mark for ${fname.trim() || "this user"}.`;
        }
      }
      if (logoCode) {
        try {
          const logoResponse = await uploadSupplierLogo(logoFile, logoCode);
          toast({ title: logoResponse.title || "Logo uploaded", description: logoContext || logoResponse.message, status: "success", duration: 4000, isClosable: true });
        } catch (logoError) {
          toast({
            title: "User created, logo upload failed",
            description: logoError?.response?.data?.message || "Add the logo from Users → Supplier Logos.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
        }
      }

      navigate("/users");
    } catch (error) {
      toast({
        title: error?.response?.data?.title || "Failed to add user",
        description: error?.response?.data?.message || "An error occurred while adding the user",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const logoSectionLabel = isSupplier ? "Supplier Logo" : isOrgAdmin ? "Organisation Logo" : "CAM Logo";
  const logoHelper = isSupplier
    ? `JPG, PNG or WEBP, max ${LOGO_MAX_BYTES / 1024 / 1024}MB. Saved as ${supplierCode || "SUPPLIERCODE"} and shown in the supplier selector immediately — no rebuild needed.`
    : isOrgAdmin
      ? `Replaces the Spear mark top-left for everyone in ${targetOrg}. Can also be set later from Users → Organisations.`
      : `Replaces the Spear mark top-left for this CAM only. JPG, PNG or WEBP, max ${LOGO_MAX_BYTES / 1024 / 1024}MB.`;

  return (
    <Box overflowX="auto">
      <Flex justify="space-between" mb={4} align="center">
        <IconButton icon={<FaArrowLeft />} colorScheme="teal" onClick={() => navigate(-1)} aria-label="Go back" />
      </Flex>
      <Box width="520px" margin="0 auto" textAlign="center" padding="4" boxShadow="md" borderRadius="md" backgroundColor="white">
        {isMaster && organisations.length > 1 && !isOrgAdmin && (
          <Text fontSize="sm" color="gray.500" mb={2}>
            Creating in organisation:{" "}
            <Text as="span" fontWeight="600" color="gray.700">
              {organisations.find((o) => o.org_code === selectedOrg)?.org_name || selectedOrg}
            </Text>
          </Text>
        )}
        <form onSubmit={handleSubmit}>
          <FormControl isRequired>
            <FormLabel>User Type</FormLabel>
            <Select value={userType} onChange={handleUserTypeChange} isDisabled={!!lockedType}>
              {USER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
            <FormHelperText textAlign="left">
              {isCam && "An account manager: views one or more suppliers, and can optionally be granted admin capabilities scoped to them."}
              {isSupplier && "The supplier's own account — creates the supplier itself (code, retailers, logo)."}
              {isOrgAdmin && "Manages one organisation: its users, articles and report sends. Retailer access follows the organisation's retailer chips."}
            </FormHelperText>
          </FormControl>

          {isOrgAdmin && (
            <FormControl isRequired mt="2">
              <FormLabel>Organisation</FormLabel>
              <Select value={targetOrg || ""} onChange={(e) => setAdminOrg(e.target.value)} placeholder={orgOptions.length ? undefined : "No organisations yet"}>
                {orgOptions.map((o) => (
                  <option key={o.org_code} value={o.org_code}>
                    {o.org_name} ({o.org_code})
                  </option>
                ))}
              </Select>
              <FormHelperText textAlign="left">Create the organisation first (Users → Organisations). Its suppliers can be added before or after this admin.</FormHelperText>
            </FormControl>
          )}

          {isSupplier ? (
            <FormControl isRequired mt="2">
              <FormLabel>Supplier Code</FormLabel>
              <Input type="text" value={codename} onChange={(e) => setCodename(e.target.value)} placeholder="ie: ADRIATIC" />
            </FormControl>
          ) : (
            <FormControl isRequired={!isInvite} mt="2">
              <FormLabel>Full Name</FormLabel>
              <Input type="text" value={fname} onChange={(e) => setFname(e.target.value)} placeholder="ie: Jane Smith" />
              {isInvite && <FormHelperText textAlign="left">Optional — they confirm their name when they register.</FormHelperText>}
            </FormControl>
          )}

          <FormControl isRequired mt="3">
            <FormLabel>Account Setup</FormLabel>
            <RadioGroup value={setupMode} onChange={setSetupMode}>
              <Stack direction="row" spacing={6}>
                <Radio value="invite">Send email invite</Radio>
                <Radio value="password">Set password now</Radio>
              </Stack>
            </RadioGroup>
            <FormHelperText textAlign="left">
              {isInvite ? "They get a registration link (valid 7 days), set their own password, and sign in with their email address." : "You choose the username and password and pass them on yourself."}
            </FormHelperText>
          </FormControl>

          <FormControl isRequired={isInvite} mt="2">
            <FormLabel>Email</FormLabel>
            <Input type="email" value={useremail} onChange={(e) => setUseremail(e.target.value)} placeholder="ie: jane@company.com" />
          </FormControl>

          {!isInvite && (
            <>
              <FormControl isRequired mt="2">
                <FormLabel>Username</FormLabel>
                <Input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ie: spearHerbal" />
              </FormControl>
              <FormControl isRequired mt="2">
                <FormLabel>Password</FormLabel>
                <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </FormControl>
            </>
          )}

          {!isOrgAdmin && (
            <FormControl mt="3">
              <FormLabel>{isSupplier ? "Retailer Access (retailers this supplier trades in)" : "Retailer Access (retailer tabs this user may open)"}</FormLabel>
              <Box>
                {RETAILERS.map((retailer) => (
                  <TogglePill key={retailer} on={!!retailerAccess[retailer.toLowerCase()]} onClick={() => handleRetailerToggle(retailer.toLowerCase())}>
                    {retailer}
                  </TogglePill>
                ))}
              </Box>
            </FormControl>
          )}

          {!isSupplier && (
            <FormControl mt="2">
              <FormLabel>Supplier Access</FormLabel>
              <Box>
                {availableSuppliers.map((supplier) => (
                  <TogglePill key={supplier} on={selectedSuppliers.includes(supplier)} onClick={() => handleSupplierToggle(supplier)}>
                    {supplier}
                  </TogglePill>
                ))}
                {availableSuppliers.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="left">
                    No suppliers in {targetOrg || "this organisation"} yet.
                  </Text>
                )}
              </Box>
              {isOrgAdmin && (
                <FormHelperText textAlign="left">
                  Optional. Every supplier created in {targetOrg || "the organisation"} is granted to its org admins automatically, so an empty organisation is fine here.
                </FormHelperText>
              )}
            </FormControl>
          )}

          <FormControl mt="2">
            <FormLabel>Page Access</FormLabel>
            <Box>
              {/* Partner-org accounts never get My Profile (backend enforces too). */}
              {reportPages
                .filter((page) => !(selectedOrg && selectedOrg !== "SPEARMGMT" && page === "MyProfile"))
                .map((page) => (
                  <TogglePill key={page} on={selectedPages.includes(page)} onClick={() => handlePageToggle(page)}>
                    {pageLabel(page)}
                  </TogglePill>
                ))}
            </Box>
          </FormControl>

          {(isCam || isOrgAdmin) && (
            <FormControl mt="2">
              <FormLabel>{isCam ? "Admin Capabilities" : "Organisation Admin Pages"}</FormLabel>
              <Box>
                {(isCam ? camAdminPages : orgAdminPages).map((page) => (
                  <TogglePill key={page} on={selectedAdminPages.includes(page)} onClick={() => handleAdminPageToggle(page)}>
                    {page === "Reports" && isOrgAdmin ? "Batch Reports" : pageLabel(page)}
                  </TogglePill>
                ))}
              </Box>
              <FormHelperText textAlign="left">
                {isCam
                  ? "Everything here is scoped to the suppliers and retailers granted above. None are granted by default."
                  : "Users = invite and manage the organisation's own viewers. Articles = edit brand, description, cost and active. Batch Reports = email a Sales Tracker. History = the organisation's own sign-ins. Alerts = run the stock alerts on demand for the organisation's suppliers. History and Alerts are not pre-selected. All scoped to the organisation."}
              </FormHelperText>
              {selectedAdminPages.includes("Articles") && (
                <Box mt={2} textAlign="left">
                  <Text fontSize="sm" mb={1}>Articles access</Text>
                  <TogglePill on={articlesLevel === "ro"} onClick={() => setArticlesLevel("ro")}>Read only</TogglePill>
                  <TogglePill on={articlesLevel === "rw"} onClick={() => setArticlesLevel("rw")}>Read + write</TogglePill>
                  <FormHelperText textAlign="left">Read only = can view the articles list but not edit brand, description, cost or the active switch. Changeable later from the Pages popover.</FormHelperText>
                </Box>
              )}
            </FormControl>
          )}

          {!isSupplier && (
            <FormControl isRequired={!isOrgAdmin} mt="2">
              <FormLabel>Default Supplier</FormLabel>
              <Select
                value={defaultSupplier}
                onChange={(e) => setDefaultSupplier(e.target.value)}
                placeholder={grantedSuppliers.length ? undefined : isOrgAdmin ? "Set automatically when the organisation's first supplier is created" : "Grant a supplier first"}
              >
                {grantedSuppliers.map((supplier) => (
                  <option key={supplier} value={supplier}>
                    {supplier}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          {!isOrgAdmin && (
            <FormControl isRequired mt="2">
              <FormLabel>Default Retailer</FormLabel>
              <Select value={defaultRetailer} onChange={(e) => setDefaultRetailer(e.target.value)} placeholder={enabledRetailers.length ? undefined : "Select a retailer first"}>
                {enabledRetailers.map((retailer) => (
                  <option key={retailer} value={retailer}>
                    {retailer}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl mt="3">
            <Flex align="center" justify="space-between">
              <FormLabel mb={0}>Payment required</FormLabel>
              <Switch isChecked={paymentRequired} onChange={(e) => setPaymentRequired(e.target.checked)} />
            </Flex>
            {paymentRequired && (
              <InputGroup mt={2}>
                <InputLeftAddon>R</InputLeftAddon>
                <Input type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="Monthly amount" />
              </InputGroup>
            )}
            <FormHelperText textAlign="left">Starts a subscription from today. Most users pay manually by EFT — record those with the Pay button on the Users page.</FormHelperText>
          </FormControl>

          <FormControl mt="2">
            <FormLabel>{logoSectionLabel}</FormLabel>
            <Flex align="center" gap={3}>
              <Box boxSize="64px" borderRadius="full" borderWidth="1px" borderColor="gray.200" overflow="hidden" display="flex" alignItems="center" justifyContent="center" bg="gray.50" flexShrink={0}>
                {logoPreview ? <Image src={logoPreview} alt="Logo preview" boxSize="64px" objectFit="contain" /> : <Text fontSize="xs" color="gray.400">No logo</Text>}
              </Box>
              <Box flex="1">
                <Input type="file" accept={LOGO_ACCEPT} onChange={handleLogoChange} p={1} border="none" />
                {logoFile && (
                  <Button size="xs" variant="link" colorScheme="red" onClick={clearLogo} mt={1}>
                    Remove
                  </Button>
                )}
              </Box>
            </Flex>
            <FormHelperText textAlign="left">{logoHelper}</FormHelperText>
          </FormControl>

          <Box mt={4}>
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
              {isInvite ? "Create & Send Invite" : "Create User"}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default AddUser;
